/**
 * RNC — TemplateEditor
 *
 * Edit a template's subject / body / tokens. Save route is PLANNED — until
 * `/v3/notifications/templates/:name` PATCH ships, we capture the intent via
 * a SessionEvent (`template-edited`) and show a success banner.
 *
 * Role-gate: SuperAdmin + Compliance only (canEditRnc).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button, Pill } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { asIso8601 } from '@retail-ops/shared-types';
import type {
  NotificationTemplate,
  SessionEventPayload,
} from '@retail-ops/shared-types';
import { canEditRnc } from './roles';
import { formatLocalTime } from '@/utils/time';

export default function TemplateEditor(): JSX.Element {
  const { templateName } = useParams<{ templateName: string }>();
  const navigate = useNavigate();
  const { config } = useConfig();
  const { user } = useAuth();
  const client = useApiClient();

  const editMode = canEditRnc(user?.primaryRole);

  const loaded = useMemo<NotificationTemplate | null>(() => {
    if (!templateName) return null;
    const src = config.notificationTemplates?.templates ?? [];
    return src.find((t) => t.templateName === templateName) ?? null;
  }, [config.notificationTemplates?.templates, templateName]);

  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [tokens, setTokens] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!loaded) return;
    setSubject(loaded.subject ?? '');
    setBody(loaded.body);
    setTokens((loaded.tokens ?? []).join(', '));
  }, [loaded]);

  if (!templateName) {
    return (
      <div className="rnc-page">
        <div className="rnc-banner rnc-banner--err">Missing template name.</div>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="rnc-page">
        <div className="rnc-banner rnc-banner--warn">
          Template <code>{templateName}</code> was not found in the loaded
          config bundle. It may not be seeded yet.
        </div>
        <div style={{ marginTop: 'var(--sp-6)' }}>
          <Button variant="ghost" onClick={() => navigate('/rnc/templates')}>
            Back to template library
          </Button>
        </div>
      </div>
    );
  }

  const handleSave = async (): Promise<void> => {
    if (!editMode) return;
    setSaving(true);

    // TODO(rnc-routes): real save target is PLANNED:
    //   PATCH /v3/notifications/templates/:name { subject, body, tokens[], version++ }
    // Once it lands, call it here and reload config.
    try {
      const ev: SessionEventPayload = {
        eventType: 'template-edited',
        eventCategory: 'system',
        severity: 'info',
        actor: user?.upn ?? 'admin',
        timestamp: asIso8601(new Date().toISOString()),
        payloadJson: JSON.stringify({
          templateName,
          nextVersion: loaded.currentVersion + 1,
          subjectChanged: subject !== (loaded.subject ?? ''),
          bodyChanged: body !== loaded.body,
        }),
      };
      await client.retail.events.write(ev).catch(() => undefined);
      setSavedAt(formatLocalTime(new Date()));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rnc-page">
      <header className="rnc-page__header">
        <div className="rnc-page__title-row">
          <h1 className="rnc-page__title">{loaded.templateName}</h1>
          <Pill variant="info">{loaded.channel}</Pill>
          <Pill variant="muted">v{loaded.currentVersion}</Pill>
          {!editMode && <Pill variant="warn">Read-only</Pill>}
        </div>
        <p className="rnc-page__subtitle">
          Category <code>{loaded.category}</code>. Save writes a new version
          row and emits a <code>template-edited</code> audit event.
        </p>
      </header>

      <div className="rnc-editor">
        <div className="rnc-banner rnc-banner--warn">
          Saving is staged only — the RNC PATCH route is PLANNED. The intent is
          captured as a SessionEvent and surfaced below.
        </div>

        <div className="rnc-editor__fields">
          <label className="rnc-editor__field-label" style={{ gridColumn: '1 / -1' }}>
            Subject
            <input
              className="rnc-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={180}
              readOnly={!editMode}
            />
          </label>
          <label className="rnc-editor__field-label" style={{ gridColumn: '1 / -1' }}>
            Body (Handlebars tokens allowed, e.g. <code>{'{{firstName}}'}</code>)
            <textarea
              className="rnc-input rnc-textarea"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              readOnly={!editMode}
            />
          </label>
          <label className="rnc-editor__field-label">
            Tokens (comma-separated)
            <input
              className="rnc-input"
              value={tokens}
              onChange={(e) => setTokens(e.target.value)}
              readOnly={!editMode}
            />
          </label>
        </div>

        {savedAt && (
          <div className="rnc-banner rnc-banner--ok">
            Edit staged at {savedAt}. A new version will be written when the
            backend route ships.
          </div>
        )}

        <div className="rnc-editor__actions">
          <Button variant="ghost" onClick={() => navigate('/rnc/templates')}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!editMode || saving}
          >
            {saving ? 'Saving…' : 'Stage save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
